/**
 * Date grouping for the chat history rail.
 *
 * Ported from skySchedule's `client/src/lib/messageDates.ts`, with the buckets
 * widened: that helper separates MESSAGES inside one conversation, where a
 * per-day header is right. This rail lists THREADS going back months, where a
 * header per calendar day would produce more headers than rows.
 *
 * ## Everything is computed in the VIEWER'S local timezone, deliberately
 *
 * That is skySchedule's note and it applies unchanged here. The rail's rows are
 * stamped from `last_activity_at`, and if grouping ran on UTC while the user
 * reads local, a chat they had at 11:58 PM last night would sit under "Today" —
 * or worse, under yesterday's date while they remember having it today. Local
 * `getFullYear/getMonth/getDate` throughout; never `toISOString()`, which would
 * shift the day for anyone east or west of UTC.
 *
 * ## The buckets are MONOTONIC, and the rail depends on that
 *
 * The rail renders a header whenever a row's label differs from the row above,
 * which is correct only if the labels are non-increasing down a list sorted by
 * `last_activity_at DESC`. These buckets are: Today ⊃ Yesterday ⊃ Previous 7
 * Days ⊃ Previous 30 Days ⊃ month-by-month, each strictly older than the last.
 * A non-monotonic bucket (say, "This week" overlapping "Yesterday") would make
 * the same header appear twice with other rows between — which reads as a
 * rendering bug rather than a grouping choice.
 */

/**
 * Stable per-calendar-day key in LOCAL time. Two timestamps share a key iff they
 * fall on the same local day.
 */
export function localDayKey(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(
    value.getDate(),
  ).padStart(2, "0")}`;
}

/** Whole local days between two instants, by calendar day rather than by 24-hour
 *  spans — so 11 PM → 1 AM is one day apart, not zero. */
function dayDelta(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((b - a) / 86_400_000);
}

/**
 * The group header a thread belongs under.
 *
 * `now` is injectable so the tests do not depend on the wall clock — the same
 * reason skySchedule's version takes it.
 *
 * Returns "" for a missing or unparseable timestamp, which the rail renders as
 * no header at all rather than as a header reading "Invalid Date".
 */
export function sessionGroupLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";

  const delta = dayDelta(d, now);

  // A clock-skewed or optimistically-stamped future row belongs at the top with
  // the newest, not under a month header below everything else.
  if (delta <= 0) return "Today";
  if (delta === 1) return "Yesterday";
  if (delta <= 7) return "Previous 7 Days";
  if (delta <= 30) return "Previous 30 Days";

  // Older than a month: group by calendar month. The year is included only when
  // it is not the current one — "August" is unambiguous this year and misleading
  // next year.
  return d.getFullYear() === now.getFullYear()
    ? d.toLocaleDateString("en-US", { month: "long" })
    : d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export interface GroupedSession<T> {
  item: T;
  /** The header to render ABOVE this row, or "" when the row above shares it. */
  header: string;
}

/**
 * Annotate a time-ordered list with the header each row should carry.
 *
 * Takes the list already sorted newest-first (which is what the server returns)
 * and does NOT re-sort it: re-sorting here would silently paper over a server
 * ordering bug, and the rail's paging depends on the server's order being the
 * real one.
 */
export function groupSessionsByDate<T>(
  items: T[],
  timestampOf: (item: T) => string | null | undefined,
  now: Date = new Date(),
): Array<GroupedSession<T>> {
  let previous = "";
  return items.map((item) => {
    const label = sessionGroupLabel(timestampOf(item), now);
    const header = label && label !== previous ? label : "";
    if (label) previous = label;
    return { item, header };
  });
}
